// src/components/ThreatProfileInfo.js
// v3.5.76: Shared helper bubble for the Threat Profile dropdown across all
// IT-flavored quote pages (Managed IT, Bundle, MultiSite). Renders a small
// (?) icon that opens a definitions reference for the four threat-profile
// tiers (None / Standard / Elevated / Critical), so reps can pick the
// right value without consulting a separate doc.
//
// v3.5.79.1: Repositioned popover to center below trigger (was overflowing
// off the left side of the viewport on QuotePage where the trigger sits in
// a narrow form column). Helped on horizontal axis but content was tall
// enough to still run off the bottom of the visible area.
//
// v3.5.79.2: Converted from inline popover to a centered modal overlay.
// The 4-tier definitions are content-heavy (~600px tall on standard zoom)
// and don't fit cleanly inline next to the trigger no matter which side
// they extend toward. Modal pattern decouples the content size from the
// trigger position — the modal is always centered on the viewport, scales
// with the screen, and scrolls internally if needed. Same content, same
// triggering button, just a more robust container.
//
// Definitions are shared with the Schedule of Services language (see
// product/legal docs and schedule_of_services.sos_summary for the
// ITSO-* rows) so what the rep sees in the UI matches what the client
// agrees to in the contract. If legal language changes, update this file
// in lockstep — wording shouldn't drift between the two surfaces.
//
// Usage:
//   <Fld lbl="Threat Profile" sub="oversight tier">
//     <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
//       <SI v={inputs.industryRisk} s={...} opts={[...]} />
//       <ThreatProfileInfo />
//     </div>
//   </Fld>

import React, { useState, useEffect } from 'react';

export default function ThreatProfileInfo() {
  const [open, setOpen] = useState(false);

  // ESC key dismisses the modal — standard a11y for modal dialogs. Bound
  // only when open to keep the listener cost zero in the steady state.
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <>
      {/* Trigger (?) icon. Sits inline next to the dropdown — same visual
          as before. Active state shifts background to dark when modal is
          open so the icon reads as "selected". */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="What do these tiers mean?"
        style={{
          background: open ? '#0f1e3c' : 'white',
          color: open ? 'white' : '#6b7280',
          border: '1px solid #d1d5db',
          borderRadius: '50%',
          width: 18, height: 18,
          fontSize: 11, fontWeight: 700,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0, lineHeight: 1,
          flexShrink: 0,
        }}>
        ?
      </button>

      {open && (
        // Modal overlay — fixed-position backdrop covers the viewport;
        // child div is the modal itself, centered via flex.
        // Click on the backdrop (not the modal) dismisses; clicks inside
        // the modal stop propagation so they don't accidentally close.
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              width: '100%',
              maxWidth: 560,
              maxHeight: 'calc(100vh - 32px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
            {/* Header — title + close button. Sticky so it stays visible
                if the body scrolls. */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 18px',
              borderBottom: '1px solid #e5e7eb',
              flexShrink: 0,
            }}>
              <h3 style={{
                margin: 0,
                fontSize: 14,
                fontWeight: 700,
                color: '#0f1e3c',
              }}>
                Threat Profile Tiers
              </h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                title="Close (Esc)"
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 22,
                  color: '#6b7280',
                  cursor: 'pointer',
                  padding: 0,
                  lineHeight: 1,
                  width: 24, height: 24,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                ×
              </button>
            </div>

            {/* Body — scrolls internally if content exceeds modal height */}
            <div style={{
              padding: '14px 18px 18px',
              overflowY: 'auto',
              fontSize: 12,
              lineHeight: 1.5,
              color: '#374151',
            }}>
              <div style={{
                fontSize: 11, color: '#6b7280', fontStyle: 'italic',
                marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid #f1f5f9',
              }}>
                Tier reflects the client's <strong>inherent threat exposure</strong> based on
                industry vertical and operational sensitivity — not the strength of their
                current defenses (which we are providing). Pick the tier that matches the
                client's risk profile, not their current security posture.
              </div>

              <Tier
                color="#6b7280"
                label="None"
                descr="Used during scoping or for clients explicitly opting out of advisory time. Produces no ITSO Advisory line on the quote. If you're unsure, default to Standard."
              />
              <Tier
                color="#2563eb"
                label="Standard"
                descr="General-purpose business with no specific regulatory framework, no high-value data assets that uniquely target them, and no operational dependency that makes downtime existentially expensive. Examples: most professional services (accounting, marketing, consulting), small retail without significant payment data, light manufacturing without OT/ICS exposure, education non-K12, nonprofits without donor-PII concentrations. Senior advisory time covers basic cyber hygiene reviews, quarterly business reviews, and routine governance."
              />
              <Tier
                color="#a16207"
                label="Elevated"
                descr="Sector where data sensitivity, regulatory pressure, or operational dependency raises the cost of a breach above baseline. Examples: healthcare (HIPAA), financial services (FINRA/SEC), legal firms (privileged data), K-12 / higher ed (FERPA + ransomware-favored target), light manufacturing with supply-chain integration, mid-market retail with PCI scope, professional services holding client PII concentrations. Senior advisory adds compliance posture reviews, vendor-risk assessments, and incident-response tabletop exercises."
              />
              <Tier
                color="#991b1b"
                label="Critical"
                descr="Sector where a breach has regulatory consequences, public disclosure requirements, or operational impact severe enough to threaten the business. Examples: hospitals and clinical care operations, broker-dealers and registered investment advisors, defense contractors (CMMC), critical infrastructure (utilities, water, OT-heavy manufacturing), large healthcare networks, organizations subject to state breach-notification laws with concentrated PII, payment processors. Senior advisory adds vCISO posture (board-level reporting), regulatory liaison support, formal IR program, and continuous compliance attestation."
              />

              <div style={{
                fontSize: 11, color: '#9ca3af',
                marginTop: 14, paddingTop: 10, borderTop: '1px solid #f1f5f9',
                fontStyle: 'italic',
              }}>
                Tier selection drives the visible ITSO Advisory line item and the
                included senior advisory hours. If the client's profile doesn't
                obviously fit one tier, talk to the rep lead before committing.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Tier({ color, label, descr }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{
          display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
          background: color,
        }} />
        <span style={{ fontSize: 13, fontWeight: 700, color }}>{label}</span>
      </div>
      <div style={{ fontSize: 12, color: '#475569', paddingLeft: 18, lineHeight: 1.55 }}>
        {descr}
      </div>
    </div>
  );
}
